import { useCallback, useRef, useState } from 'react';
import { FiCheck, FiChevronDown, FiChevronUp, FiSearch } from 'react-icons/fi';
import { FilterPill, useDismiss } from './PublicFilterDropdown';

// "Filter mode" popover from Figma (Price Range 1306:47463, Facility Type
// 1301:75466): a 236px white card with a title + "Clear Filters" row, a
// search box, checkbox rows (optionally grouped under collapsible heads) and
// a green Close button. It opens from the same pill as PublicFilterDropdown.
const ROW_RULE = 'border-b-[0.35px] border-[#a4a4a4]';

/**
 * Props
 *   label        string  — pill text
 *   title        string  — panel heading (defaults to label)
 *   activeCount  number  — selections made; shown on the pill as "(n)"
 *   onClear      fn      — "Clear Filters"
 *   align        'left' | 'right'
 *   children     (search: string) => node — rows to show for the search text
 */
export default function FilterPanel({ label, title = label, activeCount = 0, onClear, align = 'left', children }) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);

  const close = useCallback(() => setIsOpen(false), []);
  useDismiss(containerRef, isOpen, close);

  return (
    <div className="relative" ref={containerRef}>
      <FilterPill
        label={activeCount ? `${label} (${activeCount})` : label}
        isOpen={isOpen}
        onClick={() => setIsOpen((open) => !open)}
        ariaLabel={title}
        haspopup="dialog"
      />

      {isOpen && (
        <div
          role="dialog"
          aria-label={title}
          className={`absolute z-30 mt-2 flex w-[236px] flex-col items-center gap-2 rounded-[10px] border-[3px] border-[rgba(187,187,187,0.1)] bg-white py-[13px] shadow-xl ${
            align === 'right' ? 'right-0' : 'left-0'
          }`}
        >
          <div className="flex w-full flex-col items-center gap-3">
            <div className={`flex w-full items-center justify-between p-[10px] ${ROW_RULE}`}>
              <span className="text-[12px] font-medium text-[#18181b]">{title}</span>
              <button
                type="button"
                onClick={() => { onClear(); setSearch(''); }}
                className="text-[10px] font-normal text-[#28ba12] hover:underline"
              >
                Clear Filters
              </button>
            </div>

            <label className="flex w-[213px] items-center gap-2 rounded-[4px] border-[0.35px] border-[#a4a4a4] p-[10px]">
              <FiSearch className="h-3 w-3 shrink-0 text-[#28ba12]" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search"
                aria-label={`Search ${title}`}
                className="min-w-0 flex-1 bg-transparent text-[10px] font-medium text-[#18181b] outline-none placeholder:text-[#18181b]"
              />
            </label>

            <div className="max-h-[340px] w-full overflow-y-auto">{children(search.trim().toLowerCase())}</div>
          </div>

          <button
            type="button"
            onClick={close}
            className="flex w-[213px] items-center justify-center rounded-[4px] bg-[#28ba12] px-[10px] py-[7px] text-[10px] font-normal text-white transition-colors hover:bg-[#21990f]"
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
}

// One checkbox row: 10px square tick box, 12px label, faint rule below
export function FilterCheckbox({ label, checked, onChange }) {
  return (
    <label className={`flex w-full cursor-pointer items-center gap-[10px] p-[10px] ${ROW_RULE}`}>
      <input type="checkbox" checked={checked} onChange={onChange} className="peer sr-only" />
      <span
        className={`flex h-[10px] w-[10px] shrink-0 items-center justify-center rounded-[1.667px] border-[0.625px] peer-focus-visible:ring-2 peer-focus-visible:ring-[#28ba12]/40 ${
          checked ? 'border-[#28ba12] bg-[#28ba12]' : 'border-[#a4a4a4] bg-white'
        }`}
      >
        {checked && <FiCheck className="h-2 w-2 text-white" strokeWidth={4} />}
      </span>
      <span className={`text-[12px] font-medium ${checked ? 'text-[#18181b]' : 'text-[#a4a4a4]'}`}>{label}</span>
    </label>
  );
}

// Collapsible head ("Cattle", "Goat", ...) holding its own checkbox rows
export function FilterGroup({ label, isOpen, onToggle, children }) {
  return (
    <div className={`flex w-full flex-col gap-[10px] p-[10px] ${ROW_RULE}`}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        className="flex w-full items-center justify-between text-left"
      >
        <span className="text-[12px] font-medium text-[#333]">{label}</span>
        {isOpen
          ? <FiChevronUp className="h-[10px] w-[10px] shrink-0 text-[#333]" />
          : <FiChevronDown className="h-[10px] w-[10px] shrink-0 text-[#333]" />}
      </button>
      {isOpen && <div className="flex w-full flex-col gap-2">{children}</div>}
    </div>
  );
}

// Flat multi-select list (Facility Type) built on FilterPanel
export function CheckboxFilter({ label, title, options, selected, onChange, align }) {
  const toggle = (option) =>
    onChange(selected.includes(option) ? selected.filter((o) => o !== option) : [...selected, option]);

  return (
    <FilterPanel label={label} title={title} activeCount={selected.length} onClear={() => onChange([])} align={align}>
      {(search) => {
        const visible = options.filter((o) => o.toLowerCase().includes(search));
        if (!visible.length) return <p className="p-[10px] text-[12px] font-medium text-[#a4a4a4]">No matches</p>;
        return visible.map((option) => (
          <FilterCheckbox
            key={option}
            label={option}
            checked={selected.includes(option)}
            onChange={() => toggle(option)}
          />
        ));
      }}
    </FilterPanel>
  );
}
